import {
  AllieAccordion,
  AllieAccordionGroup,
  AllieAlertCircleOutlineIcon,
  AllieButton,
  AllieCheckCircleIcon,
  AllieEyeCloseIcon,
  AllieEyeOpenIcon,
  AllieHeader,
  AllieInput,
  AllieItem,
  AllieLabel,
  AllieModal,
  AllieText,
} from '@allie/app-ui-lib';
import React, { useContext, useRef, useState } from 'react';
import { AuthContext } from '../../context/AuthProvider';
import { useAllieAuth } from '../../hooks';
import './ResetPassword.scss';
import { ResetSuccess } from '../ResetSuccess';
import { ResetLoading } from '../ResetLoading';

interface ResetPasswordProps {
  email: string;
  id: number;
  modalClose: string;
  modalTitle: string;
  modalBodyTitle: string;
  modalBodySubtitle: string;
  modalBodySubtitleStrong: string;
  passwordPlaceholder: string;
  validationTitle: string;
  validationMinLengthLabel: string;
  validationLowerUpperLabel: string;
  validationNumberLabel: string;
  validationSymbolLabel: string;
  confirmPasswordPlaceholder: string;
  accordionTitle: string;
  accordionContentParagraph1: string;
  accordionContentParagraph2: string;
  resetConfirm: string;
  success: {
    modalTitle: string;
    modalBodyTitle: string;
    modalBodySubtitle: string;
    modalConfirm: string;
  };
  loading: {
    modalTitle: string;
    modalBodySubtitle: string;
  };
}

const namespace = 'reset-password';

export const ResetPassword: React.FC<ResetPasswordProps> = ({
  email,
  id,
  modalClose,
  modalTitle,
  modalBodyTitle,
  modalBodySubtitle,
  modalBodySubtitleStrong,
  passwordPlaceholder,
  validationTitle,
  validationMinLengthLabel,
  validationLowerUpperLabel,
  validationNumberLabel,
  validationSymbolLabel,
  confirmPasswordPlaceholder,
  accordionTitle,
  accordionContentParagraph1,
  accordionContentParagraph2,
  resetConfirm,
  success,
  loading,
}) => {
  const { tryGuestLogin } = useContext(AuthContext);
  const { changeEmailPassword } = useAllieAuth();
  const passwordRef = useRef<any>(null);
  const confirmPasswordRef = useRef<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [hasMinLength, setHasMinLength] = useState(false);
  const [hasLowerUpper, setHasLowerUpper] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSymbol, setHasSymbol] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const checkMinLength = () => {
    setHasMinLength(passwordRef?.current?.value.length >= 8);
  };

  const checkLowerUpper = () => {
    const validate = /(?=.*[A-Z])(?=.*[a-z])/;
    setHasLowerUpper(validate.test(passwordRef.current.value));
  };

  const checkNumber = () => {
    const validate = /(?=.*\d)/;
    setHasNumber(validate.test(passwordRef.current.value));
  };

  const checkSymbol = () => {
    const validate = /(?=.*[-+_!@#$%^&*.,?])/;
    setHasSymbol(validate.test(passwordRef.current.value));
  };

  const isMatched = () => {
    if (
      hasLowerUpper &&
      hasMinLength &&
      hasNumber &&
      hasSymbol &&
      passwordRef?.current?.value === confirmPasswordRef?.current?.value &&
      passwordRef.current.value.length > 0 &&
      confirmPasswordRef?.current?.value.length > 0
    ) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  };
  const onPasswordChange = () => {
    isMatched();
    checkMinLength();
    checkLowerUpper();
    checkNumber();
    checkSymbol();
  };

  const onConfirmPasswordChange = () => {
    isMatched();
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      await changeEmailPassword(email, confirmPasswordRef.current.value, id);
      setIsLoading(false);
      setChangePasswordSuccess(true);
    } catch (error) {}
  };

  return (
    <AllieModal backdropDismiss={false} isOpen>
      <div className={namespace}>
        {!changePasswordSuccess && !isLoading && (
          <>
            <AllieHeader className={`${namespace}-header`}>
              <AllieText type="h3">{modalTitle}</AllieText>
              <AllieButton
                corner="rounded-edges"
                allieSize="small"
                allieType="subtle"
                allieColor="neutral"
                onClick={() => tryGuestLogin?.(true)}
              >
                {modalClose}
              </AllieButton>
            </AllieHeader>
            <div className={`${namespace}-body`}>
              <div className={`${namespace}-body-text`}>
                <AllieText type="h4" color="neutral-80">
                  {modalBodyTitle}
                </AllieText>
              </div>
              <div className={`${namespace}-body-text`}>
                <AllieText color="neutral-80">
                  {modalBodySubtitle}
                  <strong>{modalBodySubtitleStrong}</strong>
                </AllieText>
              </div>
              <AllieInput
                ref={passwordRef}
                placeholder={passwordPlaceholder}
                type={showPassword ? 'text' : 'password'}
                corner="rounded-edges"
                clearInput={true}
                onChange={onPasswordChange}
                allieSize="small"
                icon={showPassword ? <AllieEyeOpenIcon /> : <AllieEyeCloseIcon />}
                onClick={handleShowPassword}
              />
              <div className={`${namespace}-body-text`}>
                <AllieText type="body3" color="neutral-50">
                  {validationTitle}
                </AllieText>
                <div
                  className={`${namespace}-input-validation-${hasMinLength ? 'success' : 'failed'}`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body3" color="neutral-50">
                    {validationMinLengthLabel}
                  </AllieText>
                </div>
                <div
                  className={`${namespace}-input-validation-${
                    hasLowerUpper ? 'success' : 'failed'
                  }`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body3" color="neutral-50">
                    {validationLowerUpperLabel}
                  </AllieText>
                </div>
                <div
                  className={`${namespace}-input-validation-${hasNumber ? 'success' : 'failed'}`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body3" color="neutral-50">
                    {validationNumberLabel}
                  </AllieText>
                </div>
                <div
                  className={`${namespace}-input-validation-${hasSymbol ? 'success' : 'failed'}`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body3" color="neutral-50">
                    {validationSymbolLabel}
                  </AllieText>
                </div>
              </div>
              <AllieInput
                ref={confirmPasswordRef}
                placeholder={confirmPasswordPlaceholder}
                type={showPassword ? 'text' : 'password'}
                corner="rounded-edges"
                clearInput={true}
                onChange={onConfirmPasswordChange}
                allieSize="small"
                icon={showPassword ? <AllieEyeOpenIcon /> : <AllieEyeCloseIcon />}
                onClick={handleShowPassword}
              />
            </div>
            <AllieAccordionGroup expand="inset" className={`${namespace}-accordion`}>
              <AllieAccordion>
                <AllieItem className={`${namespace}-accordion-header`} slot="header">
                  <AllieAlertCircleOutlineIcon allieColor="success" />
                  <AllieLabel>{accordionTitle}</AllieLabel>
                </AllieItem>
                <div className={`${namespace}-accordion-content`} slot="content">
                  <AllieText type="body2" color="neutral-40">
                    {accordionContentParagraph1}
                    <br />
                    <br />
                    {accordionContentParagraph2}
                  </AllieText>
                </div>
              </AllieAccordion>
            </AllieAccordionGroup>
            <div className={`${namespace}-confirm`}>
              <AllieButton
                onClick={onSubmit}
                corner="rounded-edges"
                allieColor="primary"
                allieSize="small"
                disabled={isDisable}
              >
                {resetConfirm}
              </AllieButton>
            </div>
          </>
        )}
        {changePasswordSuccess && <ResetSuccess {...success} id={id} />}
        {isLoading && <ResetLoading {...loading} />}
      </div>
    </AllieModal>
  );
};
